// ============================================================================
// folhaRecibosApi — recibos da folha liberados para a gerente (/api/folha-recibos)
// ============================================================================
// Pedido do dono (05/10/2026): a Beatriz (gerente Vila + Itaim) tira, no Banco
// de Horas, os recibos de pagamento das funcionárias das unidades dela.
//
// Por que SNAPSHOT: a folha calcula cada linha AO VIVO no Financeiro (comissão,
// bônus, banco de horas…) e o payroll_records não guarda esses valores. Então o
// operador da folha PUBLICA uma foto congelada da linha (ao marcar como paga ou
// pelo botão "Liberar recibos"), e a gerente só lê essa foto — ela nunca vê a
// folha nem recalcula nada. Quem pode ver o quê é decidido no SERVIDOR.
//
// REGRA LINEAR: falhou → lança Error com a mensagem do servidor (a tela mantém
// o estado anterior e avisa). Nunca "lista vazia" silenciosa.
// ============================================================================

import { sessionToken } from './secureRest.js';
import { valesParaRecibo } from './folhaVales.js';

const BASE = '/api/folha-recibos';
const MES_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const LOTE_MAX = 300; // teto do servidor por POST

/** 'YYYY-MM' do mês do pagamento. */
export function mesKeyDe(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

async function call(method, params) {
  const token = sessionToken();
  if (!token) throw new Error('Sessão expirada. Entre de novo.');
  const url = method === 'GET' ? `${BASE}?${new URLSearchParams(params)}` : BASE;
  let r;
  try {
    r = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-session-token': token },
      body: method === 'GET' ? undefined : JSON.stringify(params),
      cache: 'no-store',
    });
  } catch {
    throw new Error('Sem conexão com o servidor. Tente de novo em instantes.');
  }
  const text = await r.text().catch(() => '');
  const trimmed = text.trim();
  let j = null;
  if (trimmed && !trimmed.startsWith('<')) {
    try { j = JSON.parse(trimmed); } catch { j = null; }
  }
  if (!r.ok) {
    const e = new Error((j && j.error) || `Erro ${r.status} ao falar com o servidor.`);
    e.status = r.status;
    throw e;
  }
  if (!j || typeof j !== 'object') {
    // HTML/fallback servido no lugar da API (deploy, SW, plataforma)
    throw new Error('Resposta inesperada do servidor (tente de novo).');
  }
  return j;
}

/** → { pode, escopo: 'rede'|'unidades'|null, unitIds: string[]|null, podePublicar } */
export async function acessoRecibos() {
  const j = await call('GET', { op: 'acesso' });
  return {
    pode: j.pode === true,
    escopo: j.escopo === 'rede' || j.escopo === 'unidades' ? j.escopo : null,
    unitIds: Array.isArray(j.unitIds) ? j.unitIds.map(String) : null,
    podePublicar: j.podePublicar === true,
  };
}

/** → { mes, recibos: [snapshot...] } (já filtrado pelo escopo no servidor) */
export async function listarRecibos(mes) {
  if (!MES_RE.test(String(mes || ''))) throw new Error('Mês inválido.');
  const j = await call('GET', { op: 'listar', mes });
  if (!Array.isArray(j.recibos)) throw new Error('Resposta inesperada do servidor (lista de recibos ausente).');
  return { mes: j.mes || mes, recibos: j.recibos };
}

/** Publica (sobrescreve) os recibos do mês. → { ok, publicados } */
export async function publicarRecibos(mes, recibos) {
  if (!MES_RE.test(String(mes || ''))) throw new Error('Mês inválido.');
  const lista = (Array.isArray(recibos) ? recibos : []).filter(Boolean);
  if (!lista.length) return { ok: true, publicados: 0 };
  let publicados = 0;
  for (let i = 0; i < lista.length; i += LOTE_MAX) {
    const parte = lista.slice(i, i + LOTE_MAX);
    const j = await call('POST', { op: 'publicar', mes, recibos: parte });
    publicados += Number(j.publicados) || 0;
  }
  return { ok: true, publicados };
}

/** Remove os recibos dos profIds no mês (inexistente = ok). → { ok, removidos } */
export async function removerRecibos(mes, profIds) {
  if (!MES_RE.test(String(mes || ''))) throw new Error('Mês inválido.');
  const ids = [...new Set((Array.isArray(profIds) ? profIds : []).filter(Boolean).map(String))];
  if (!ids.length) return { ok: true, removidos: 0 };
  const j = await call('POST', { op: 'remover', mes, profIds: ids });
  return { ok: true, removidos: Number(j.removidos) || 0 };
}

// ─── Snapshot da linha da folha ─────────────────────────────────────────────
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const str = (v) => (v == null ? '' : String(v)).trim();
const dataIso = (v) => { const m = String(v || '').match(/^(\d{4}-\d{2}-\d{2})/); return m ? m[1] : ''; };

/**
 * Monta o snapshot publicado a partir da MESMA linha calculada da folha
 * (o objeto de `rows` do FolhaPagamentoModule — o mesmo destructuring do botão
 * do holerite). Só valores já calculados: nada é recalculado aqui.
 *
 * @param {Object} linha  { emp, empResolved, effective, comissao, bonusAuto, bonusBreakdown,
 *                          liquido, provisao, dedZerado, bancoExtras, bancoDescontos,
 *                          extraMinTotal, status, transferencia, cartao, especie,
 *                          vale?, valesLinha? }
 * @param {{ year:number, month:number }} periodo  mês do PAGAMENTO da folha
 *
 * Vales (financeiro-013, 20/10/2026): o recibo leva o vale descontado neste
 * pagamento (row.vale, reais) e a lista (row.vales = [{dia, valor, referencia}])
 * para a linha "24 Adiantamento (vale)"; o líquido e a transferência da linha já
 * vêm sem o vale, então o recibo fecha. Sem vale: vale 0 e lista vazia.
 */
export function montarSnapshotRecibo(linha, { year, month }) {
  const {
    emp = {}, empResolved = {}, effective = {}, comissao, bonusAuto, bonusBreakdown,
    liquido, provisao, dedZerado, bancoExtras, bancoDescontos, extraMinTotal,
    status, transferencia, cartao, especie, valesLinha,
  } = linha || {};
  const vales = valesParaRecibo(valesLinha);
  const vale = Math.round(vales.reduce((a, v) => a + v.valor * 100, 0)) / 100;
  const e = { ...emp, ...empResolved }; // empResolved já vem com admissão/benefícios resolvidos
  const unitId = empResolved.unitId || emp.unitId || 'geral';
  return {
    profId: emp.professionalId || empResolved.professionalId || null,
    empId: emp.id || null,
    unitId,
    year: Number(year),
    month: Number(month),
    status: status === 'paga' ? 'paga' : 'pendente',
    emp: {
      name: str(e.name),
      cpf: str(e.cpf),
      cargo: str(e.cargo),
      contractType: str(e.contractType) || 'CLT',
      unitId,
      dataAdmissao: dataIso(e.dataAdmissao),
      salarioBase: num(e.salarioBase),
      vtValor: num(e.vtValor),
      vrValor: num(e.vrValor),
      ajudaCusto: num(e.ajudaCusto),
      inssValor: num(e.inssValor),
      vtDescontoPct: num(e.vtDescontoPct),
    },
    row: {
      effective: {
        desconto: num(effective.desconto),
        feriadosQtd: num(effective.feriadosQtd),
        feriadosValor: num(effective.feriadosValor),
        bonus: num(effective.bonus),
      },
      comissao: num(comissao),
      bonusAuto: num(bonusAuto),
      bonusBreakdown: (Array.isArray(bonusBreakdown) ? bonusBreakdown : [])
        .slice(0, 20)
        .map(b => ({ label: str(b?.label).slice(0, 80), valor: num(b?.valor) })),
      liquido: num(liquido),
      provisao: num(provisao),
      dedZerado: !!dedZerado,
      bancoExtras: num(bancoExtras),
      bancoDescontos: num(bancoDescontos),
      extraMinTotal: num(extraMinTotal),
      transferencia: num(transferencia),
      cartao: num(cartao),
      especie: num(especie),
      vale,
      vales,
    },
  };
}
